/* eslint-disable react/prop-types */
/**
 * ChecklistItemPreviewRow — read-only render of a projected checklist
 * item from the BE's ``checklist_specs`` payload (PR2 transform layer).
 *
 * Tasks V2 Migration v4 plan, PR6.
 *
 * Used by:
 *   * TaskDetailChecklist mode='preview' (this PR)
 *   * DryRunReminderPreviewPanel right pane (PR7)
 *   * ExecuteResultsPanel drift indicator (PR8)
 *
 * Spec shape (from PR2 ``transform_reminder_to_task``):
 *   {
 *     template_item_id: '<uuid>',
 *     title: 'Call the lead' | '[CHANNEL] action',
 *     item_type: 'PHONE_CALLED' | 'CONFIRMATION' | ...,
 *     sort_order: 0,
 *     due_at: '2026-05-08T12:00:00Z' | null,
 *     skip: false,
 *     skip_reason: 'WILL_SKIP_ANCHOR_UNRESOLVABLE' | undefined,
 *     metadata: { role: 'v1_method_channel'?, role_gated: true? },
 *     action_config: { ... },
 *   }
 *
 * No redux, no fetch, no completion controls -- this is a preview.
 */
import {
    Box, Stack, Typography, Chip, Tooltip,
} from '@mui/material';

import Iconify from 'src/components/Iconify';
import { parseApiDate } from 'src/utils/taskDateUtils';


const ITEM_TYPE_LABEL = {
    CONFIRMATION:        'Confirmation',
    EMAIL_SENT:          'Email',
    WHATSAPP_SENT:       'WhatsApp',
    SMS_SENT:            'SMS',
    PHONE_CALLED:        'Phone',
    LOG_PHONE_CALL:      'Log call',
    SEND_PAYMENT_LINK:   'Payment link',
    SEND_CONSENT_FORM:   'Consent form',
    SEND_REVIEW_REQUEST: 'Review request',
    ADD_NOTE:            'Note',
    BOOK_APPOINTMENT:    'Book appointment',
    LEAD_NEXT_ACTION:    'Next action',
    SCHEDULE_REMINDER:   'Schedule reminder',
    OPEN_FOLLOW_UP:      'Follow-up',
    LEAD_STATUS_CHANGED: 'Status change',
    UPLOAD_DOCUMENT:     'Upload',
    LINKED_CHECKLIST:    'Linked checklist',
    FREE_TEXT:           'Free text',
    MULTIPLE_CHOICE:     'Multiple choice',
};


function _formatDueAt(dueIso) {
    if (!dueIso) return null;
    try {
        const d = parseApiDate(dueIso)?.toDate() || new Date(dueIso);
        return d.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch (_e) {
        return null;
    }
}


export default function ChecklistItemPreviewRow({ spec }) {
    if (!spec) return null;

    const isSkipped = Boolean(spec.skip);
    const isChannelSlot = (spec.metadata || {}).role === 'v1_method_channel';
    const isRoleGated = Boolean((spec.metadata || {}).role_gated);
    const itemTypeLabel = ITEM_TYPE_LABEL[spec.item_type] || spec.item_type || 'Step';
    const dueLabel = _formatDueAt(spec.due_at);

    return (
        <Box
            data-testid={`checklist-item-preview-${spec.template_item_id || spec.sort_order}`}
            sx={{
                py: 1,
                px: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: isSkipped ? 0.5 : 1,
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            <Stack direction="row" spacing={1.25} alignItems="center">
                {/* Read-only checkbox-shaped icon to signal "preview, not actionable". */}
                <Iconify
                    icon={isSkipped ? 'mdi:checkbox-blank-off-outline' : 'mdi:checkbox-blank-outline'}
                    width={18}
                    sx={{ color: 'text.disabled' }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                textDecoration: isSkipped ? 'line-through' : 'none',
                            }}
                        >
                            {spec.title || '(untitled)'}
                        </Typography>

                        {/* item_type chip */}
                        <Chip
                            label={itemTypeLabel}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />

                        {/* channel-slot tag */}
                        {isChannelSlot && (
                            <Tooltip title="This slot is patched per the V1 reminder's method (call/WhatsApp/email/...).">
                                <Chip
                                    label="Channel"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                            </Tooltip>
                        )}

                        {/* role-gated tag (e.g. SEND_PAYMENT_LINK) */}
                        {isRoleGated && (
                            <Tooltip title="Completion of this item requires an elevated practice role (e.g. payment handler).">
                                <Chip
                                    label="Role-gated"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                            </Tooltip>
                        )}

                        {/* skip badge */}
                        {isSkipped && (
                            <Tooltip
                                title={
                                    spec.skip_reason
                                        ? `Skipped: ${spec.skip_reason.replace(/_/g, ' ').toLowerCase()}`
                                        : 'Skipped during projection.'
                                }
                            >
                                <Chip
                                    label="Skipped"
                                    size="small"
                                    color="default"
                                    icon={<Iconify icon="mdi:skip-forward-outline" width={12} />}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                            </Tooltip>
                        )}
                    </Stack>

                    {/* Projected due_at -- only when not skipped. */}
                    {dueLabel && !isSkipped && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                        >
                            Due {dueLabel}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
